import type { ChangeEvent, FocusEvent, WheelEvent } from "react";
import { useEffect, useState } from "react";

interface NumberFieldProps {
  id: string;
  className?: string;
  label: string;
  value: number | undefined;
  min?: number;
  max?: number;
  step?: number;
  onChange: (value: number | undefined) => void;
}

export function NumberField({
  id,
  className,
  label,
  value,
  min = 0,
  max,
  step = 1,
  onChange
}: NumberFieldProps) {
  const [draftValue, setDraftValue] = useState(formatNumberForInput(value));

  useEffect(() => {
    setDraftValue(formatNumberForInput(value));
  }, [value]);

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const nextValue = event.currentTarget.value;
    setDraftValue(nextValue);
    onChange(nextValue === "" ? undefined : event.currentTarget.valueAsNumber);
  }

  function handleBlur(event: FocusEvent<HTMLInputElement>) {
    const rawValue = event.currentTarget.value;
    if (rawValue === "") {
      return;
    }

    const parsedValue = event.currentTarget.valueAsNumber;
    const normalizedValue = clampNumber(parsedValue, min, max);
    setDraftValue(formatNumberForInput(normalizedValue));
    if (normalizedValue !== value) {
      onChange(normalizedValue);
    }
  }

  function handleWheel(event: WheelEvent<HTMLInputElement>) {
    // Native number inputs change value on wheel while focused. In this dense
    // workspace that can silently alter discounts/rates while the user scrolls.
    event.currentTarget.blur();
  }

  return (
    <label className={["field", className].filter(Boolean).join(" ")} htmlFor={id}>
      <span>{label}</span>
      <input
        id={id}
        type="number"
        min={min}
        max={max}
        step={step}
        value={draftValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onWheel={handleWheel}
      />
    </label>
  );
}

function clampNumber(value: number, min: number | undefined, max: number | undefined): number {
  if (!Number.isFinite(value)) {
    return min ?? 0;
  }

  const minValue = min ?? Number.NEGATIVE_INFINITY;
  const maxValue = max ?? Number.POSITIVE_INFINITY;
  return Math.min(maxValue, Math.max(minValue, value));
}

function formatNumberForInput(value: number | undefined): string {
  return Number.isFinite(value) ? String(value) : "";
}
